//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';

import {
  IAliasedDateRange,
  getReadableDateRangeValue,
} from '@components/date-range-picker/dateRangePickerModels';
import { IStatBubbleComparisonStat } from '@components/molecules/stat-bubble/StatBubble';
import DateString from '@models/DateString';
import { isDefined } from '@utils/filterPredicates';
import { readableRate } from '@utils/readableNumber';

/**
 * @param overrideDifferenceStat is an optional manual override for difference calculation.
 * - When provided, displays this exact value instead of calculating (stat - comparisonStat)
 * - Affects percentage display and trend direction in the comparison chip
 */
export interface IGetComparisonStat {
  overrideDifferenceStat?: number;
  stat: number | undefined;
  comparisonStat: number | undefined;
  title: string;
  readableNumber: string;
  dateRange: IAliasedDateRange;
  comparisonDateRange?: IAliasedDateRange;
  isPlural?: boolean;
}

export const getComparisonStat = ({
  comparisonDateRange,
  comparisonStat,
  dateRange,
  isPlural = false,
  overrideDifferenceStat,
  readableNumber,
  stat,
  title,
}: IGetComparisonStat): IStatBubbleComparisonStat | undefined => {
  if (
    !isDefined(stat) ||
    !isDefined(comparisonStat) ||
    readableNumber === '--' ||
    dateRange.displayName === 'All Time'
  ) {
    return undefined;
  } else {
    const percentageValue = isDefined(overrideDifferenceStat)
      ? readableRate(Math.abs(overrideDifferenceStat), { rateDecimals: 1 })
      : getComparisonPercentage(stat, comparisonStat);
    return {
      percentage: percentageValue,
      trendingIcon: getTrendingIcon(stat, comparisonStat),
      tooltipText: getComparisonTooltip({
        overrideDifferenceStat: overrideDifferenceStat,
        stat,
        comparisonStat,
        title,
        dateRange,
        comparisonDateRange,
        isPlural,
      }),
    };
  }
};

export const getComparisonPercentage = (
  stat: number,
  comparisonStat: number,
): string => {
  if (comparisonStat === 0) {
    return '%';
  }

  return readableRate(
    Math.abs((stat - comparisonStat) / comparisonStat) * 100,
    { rateDecimals: 1 },
  );
};

export const getTrendingIcon = (
  stat: number,
  comparisonStat: number,
): UnisonIconType => {
  if (stat === comparisonStat || comparisonStat === 0) {
    return 'collapse';
  } else if (stat > comparisonStat) {
    return 'trending-up';
  } else {
    return 'trending-down';
  }
};

interface IGetIntervalComparisonTooltipLanguage {
  title: string;
  interval: string;
  direction: string;
  percentage: string;
  isPlural: boolean;
}

export const getIntervalComparisonTooltipLanguage = ({
  direction,
  interval,
  isPlural,
  percentage,
  title,
}: IGetIntervalComparisonTooltipLanguage): string => {
  if (percentage === '%') {
    return 'We cannot calculate percentage growth for this time period as the previous value for your date range is zero.';
  } else if (percentage === '0%') {
    return `There was no change in your ${title} across the last ${interval} compared to the previous ${interval}.`;
  } else {
    return `Your ${title} across the last ${interval} ${
      isPlural ? 'were' : 'was'
    } ${direction} ${percentage} compared to the previous ${interval}.`;
  }
};

interface IGetCustomRangeComparisonTooltipLanguage {
  title: string;
  interval: string;
  comparisonInterval: string;
  direction: string;
  percentage: string;
  isPlural: boolean;
}

export const getCustomRangeComparisonTooltipLanguage = ({
  comparisonInterval,
  direction,
  interval,
  isPlural,
  percentage,
  title,
}: IGetCustomRangeComparisonTooltipLanguage): string => {
  if (percentage === '%') {
    return 'We cannot calculate percentage growth for this time period as the previous value for your date range is zero.';
  } else if (percentage === '0%') {
    return `There was no change in your ${title} across ${interval} compared to ${comparisonInterval}.`;
  } else {
    return `Your ${title} across ${interval} ${
      isPlural ? 'were' : 'was'
    } ${direction} ${percentage} compared to ${comparisonInterval}.`;
  }
};

interface IGetComparisonTooltip {
  overrideDifferenceStat?: number;
  stat: number;
  comparisonStat: number;
  title: string;
  dateRange: IAliasedDateRange;
  comparisonDateRange?: IAliasedDateRange;
  isPlural: boolean;
}

export const getComparisonTooltip = ({
  comparisonDateRange,
  comparisonStat,
  dateRange,
  isPlural,
  overrideDifferenceStat,
  stat,
  title,
}: IGetComparisonTooltip): string | undefined => {
  const direction =
    (overrideDifferenceStat ?? stat - comparisonStat) >= 0 ? 'up' : 'down';

  const percentage = isDefined(overrideDifferenceStat)
    ? readableRate(Math.abs(overrideDifferenceStat), { rateDecimals: 1 })
    : getComparisonPercentage(stat, comparisonStat);

  if (dateRange.displayName === 'Custom') {
    const dayDiff = DateString.getDayOffset(
      dateRange.endDate,
      dateRange.startDate,
    );
    const comparisonEndDate = DateString.addDays(dateRange.startDate, -1);

    const comparisonInterval = comparisonDateRange
      ? getReadableDateRangeValue(comparisonDateRange)
      : getReadableDateRangeValue({
          ...dateRange,
          startDate: DateString.addDays(comparisonEndDate, dayDiff),
          endDate: comparisonEndDate,
        });

    return getCustomRangeComparisonTooltipLanguage({
      title,
      interval: getReadableDateRangeValue(dateRange).toLowerCase(),
      comparisonInterval,
      direction,
      percentage,
      isPlural,
    });
  }

  return getIntervalComparisonTooltipLanguage({
    title,
    interval: getReadableDateRangeValue(dateRange)
      .replace('Last ', '')
      .toLowerCase(),
    direction,
    percentage,
    isPlural,
  });
};

export const getComparisonStatSectionTrackingId = (prefix?: string): string => {
  return `${prefix ?? ''}stat-section-with-comparison-stats`;
};
